'use client';
import { AdvancedMatchesFeedWidget } from '@/components/AdvancedMatchesFeedWidget';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ArticleCard } from '@/components/cards/articles/ArticleCard';
import { FeaturedArticleCard } from '@/components/cards/articles/FeaturedArticleCard';
import { InfiniteCarousel } from '@/components/carousels/infinite';
import { EmptyState } from '@/components/EmptyState';
import { SplitPageLayout } from '@/components/layout/SplitPageLayout';
import { MainPageTitle } from '@/components/MainPageTitle';
import { PageSectionTitle } from '@/components/PageSectionTitle';
import { Pagination } from '@/components/pagination';
import { usePaginationNavigation } from '@/hooks/usePaginationNavigation.hook';
import { DEFAULT_ARTICLES_PER_PAGE } from '@/strapi/articles.util';
import type { Article } from '@/types/article';
import { sortStrapiItemsDesc } from '@/utils/strapi';

interface NewsPageProps {
  articles: {
    data: Article[];
    meta: { pagination: { pageCount: number; total: number } };
  };
  featuredArticles: { data: Article[] };
  currentPage: number;
}

export const NewsPage = ({
  articles,
  featuredArticles,
  currentPage,
}: NewsPageProps) => {
  const { handlePageChange } = usePaginationNavigation({
    scrollTargetElementId: 'articles',
  });

  const sortedFeaturedArticles = sortStrapiItemsDesc(featuredArticles.data);

  return (
    <>
      <Breadcrumbs breadcrumbs={[{ title: 'News' }]} className="mb-5" />

      <MainPageTitle>News</MainPageTitle>

      {sortedFeaturedArticles.length > 0 && (
        <div className="mb-[30px] flex flex-col items-center gap-5 md:mb-[60px]">
          <div className="hidden w-full md:block">
            <InfiniteCarousel slideWidthPercentage={45}>
              {sortedFeaturedArticles.map((item, index: number) => (
                <FeaturedArticleCard
                  data={item}
                  key={`featured-${item.id || index}`}
                />
              ))}
            </InfiniteCarousel>
          </div>
          <div className="flex flex-col gap-4 md:hidden">
            {sortedFeaturedArticles.map((item, index: number) => (
              <FeaturedArticleCard
                data={item}
                key={`featured-mobile-${item.id || index}`}
              />
            ))}
          </div>
        </div>
      )}

      <PageSectionTitle className="mb-5" id="articles">
        All articles
      </PageSectionTitle>

      <SplitPageLayout>
        {/* COL 1 */}
        <div className="flex flex-col gap-4 rounded-lg">
          <div className="flex flex-col gap-3">
            {articles.data.map((article) => (
              <ArticleCard
                article={article}
                className="rounded-xl bg-medium-gray p-4"
                key={article.id}
              />
            ))}
          </div>
          {articles.data.length === 0 && (
            <div className="flex items-center justify-center gap-2 rounded-lg bg-card-bg px-3 py-5">
              <EmptyState
                description="We couldn't find any articles right now. Please check back later."
                title="No articles found"
              />
            </div>
          )}
          <Pagination
            currentPage={currentPage}
            onChange={handlePageChange}
            perPageItems={DEFAULT_ARTICLES_PER_PAGE}
            // TODO: don't hard code this number
            totalCount={articles.meta.pagination.total}
            totalPages={articles.meta.pagination.pageCount}
          />
        </div>
        {/* COL 2 */}
        <div className="flex flex-col gap-5">
          <AdvancedMatchesFeedWidget type="finished" />
          <AdvancedMatchesFeedWidget type="not_started" />
        </div>
      </SplitPageLayout>
    </>
  );
};
